import React, { useState, useEffect, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import axios from 'axios';
import {
    Container,
    TextField,
    IconButton,
    Popover,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import _ from 'lodash';
import { NoDataPlaceholder } from '../../components/Placeholders/NoDataPlaceholder';
import { HeaderContainer, HeaderWrapper } from '../../CampaignDetails/CampaignDetails.styles';
import { MobileWrapper } from '../../Campaigns/Campaigns.styles';
import { PaginationWrapper } from '../../Payments/Payments.styles';
import { DateButton, DateWrapper } from '../../Payments/Payments.styles'
import { useAuth } from '../../context/auth';
import { Placeholders } from '../../components/Placeholders';
import { QuestionType } from './Question/Question.interface';
import { Question } from './Question';
import { SupportAdminApiResponse } from './SupportAdmin.interface';
import { InfoRow, InputWrapper } from './SupportAdmin.styles';

export function SupportAdmin() {
    const { authToken } = useAuth();
    const [searchSubject, setSearchSubject] = useState<{id: number, name: string} | null>();
    const [searchUser, setSearchUser] = useState('');
    const [searchDateFrom, setSearchDateFrom] = useState<null | Date>(null);
    const [searchDateTo, setSearchDateTo] = useState<null | Date>(null);
    const [searchAnswered, setSearchAnswered] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [subjectList, setSubjectList] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalResults, setTotalResults] = useState(0);
    const search = (subject?: any, user?: string, dateFrom?: string, dateTo?: string, answered?: string, page?: number, rows?: number) => {
        let url = `${process.env.REACT_APP_BASE_URL}/api/support/questions/`;
        setIsLoading(true);
        if (subject) {
            url += `${url.includes('?') ? '&' : '?'}subject=${subject.id}`
        }
        if (user) {
            url += `${url.includes('?') ? '&' : '?'}user=${encodeURIComponent(user)}`
        }
        if (dateFrom) {
            const date = moment(dateFrom).format("YYYY-MM-DD");
            url += `${url.includes('?') ? '&' : '?'}date_after=${date}`
        }
        if (dateTo) {
            const date = moment(dateTo).format("YYYY-MM-DD");
            url += `${url.includes('?') ? '&' : '?'}date_before=${date}`
        }
        if (answered) {
            url += `${url.includes('?') ? '&' : '?'}answered=${answered}`
        }
        if (page && page !== 0) {
            url += `${url.includes('?') ? '&' : '?'}page=${page + 1}`
        }
        if (rows && rows !== 10) {
            url += `${url.includes('?') ? '&' : '?'}page_size=${rows}`
        }
        if (subject || user  || dateFrom || dateTo || answered) {
            setIsSearching(true);
        }
        else {
            setIsSearching(false);
        }
        axios.get(url, {headers: {DTOKEN: `${authToken}`}}).then(res => {
            const results = res.data.results.map((question: SupportAdminApiResponse) => {
                return {
                    ...question,
                    campaignTitle: question.campaign_title,
                    createDate: question.create_date,
                    adminAnswer: question.admin_answer,
                    adminUser: question.admin_user
                }
            })
            setTotalResults(res.data.count)
            setQuestions(results);
            setIsLoading(false);
        })
    }
    const delayedQuery = useCallback(_.debounce((subject, user, dateFrom, dateTo, answered, page, rows) => {
        search(subject, user, dateFrom, dateTo, answered, page, rows);
    }, 500), []);
    useEffect(() => {
        setPage(0);
    }, [searchSubject, searchUser, searchDateFrom, searchDateTo, searchAnswered])
    useEffect(() => {
        window.scrollTo(0, 0);
        delayedQuery(searchSubject, searchUser, searchDateFrom, searchDateTo, searchAnswered, page, rowsPerPage)
    }, [searchSubject, searchUser, searchDateFrom, searchDateTo, searchAnswered, page, rowsPerPage])
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/support/questions/subjects/`, {headers: { DTOKEN: `${authToken}`}}).then(res => {
            setSubjectList(res.data)
        })
    }, [])
    const handleDateFromChange = (date: any) => {
        setSearchDateFrom(date);
    };
    const handleDateToChange = (date: any) => {
        setSearchDateTo(date);
    };
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleChangeAnswered = (event: React.ChangeEvent<{ value: unknown }>) => {
        setSearchAnswered(event.target.value as string);
    };
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    return (
        <>
            <HeaderWrapper>
                <HeaderContainer>
                    <h1>Support panel</h1>
                </HeaderContainer>
            </HeaderWrapper>
            <Container>
                <InputWrapper>
                    <TextField
                        variant="outlined" 
                        placeholder="User"
                        value={searchUser}
                        onChange={(event) => setSearchUser(event.target.value)}
                    />
                    <Autocomplete
                        value={searchSubject}
                        onChange={(event: any, newValue: {id: number, name: string} | null) => {
                            setSearchSubject(newValue);
                        }}
                        getOptionLabel={(option: any) => option.name}
                        renderOption={(option: any) => option.name}
                        options={subjectList}
                        renderInput={(params) => <TextField {...params} placeholder="Subject" variant="outlined" />}
                    />
                    <DateButton onClick={handleClick}>
                        {(searchDateFrom && !searchDateTo) && `From: ${moment(searchDateFrom).format("DD MMM YYYY")}`}
                        {(!searchDateFrom && searchDateTo) && ` To: ${moment(searchDateTo).format("DD MMM YYYY")}`}
                        {(searchDateFrom && searchDateTo) && `${moment(searchDateFrom).format("DD MMM YY")} - ${moment(searchDateTo).format("DD MMM YY")}`}
                        {(!searchDateFrom && !searchDateTo) &&
                            <span>Date range</span>
                        }
                        {(searchDateFrom || searchDateTo) && 
                            <IconButton onClick={() => {
                                setSearchDateFrom(null);
                                setSearchDateTo(null);
                                setAnchorEl(null);
                            }}>
                                <Close />
                            </IconButton>
                        }
                    </DateButton>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <DateWrapper>
                            <DatePicker
                                placeholder="Date from"
                                variant="inline"
                                value={searchDateFrom}
                                format="DD MMM YYYY"
                                onChange={handleDateFromChange}
                                InputProps={{
                                    endAdornment: (
                                    <IconButton onClick={() => setSearchDateFrom(null)}>
                                        <Close />
                                    </IconButton>
                                    )
                                }}
                            />
                            <DatePicker
                                placeholder="Date to"
                                variant="inline"
                                value={searchDateTo}
                                format="DD MMM YYYY"
                                onChange={handleDateToChange}
                                InputProps={{
                                    endAdornment: (
                                    <IconButton onClick={() => setSearchDateTo(null)}>
                                        <Close />
                                    </IconButton>
                                    )
                                }}
                            />
                        </DateWrapper>
                    </Popover>
                    <FormControl variant="outlined">
                        <InputLabel htmlFor="answeredSelect">Answered</InputLabel>
                        <Select
                            label="Answered"
                            value={searchAnswered}
                            onChange={handleChangeAnswered}
                            inputProps={{
                                name: 'answered',
                                id: 'answeredSelect',
                            }}
                        >
                            <MenuItem key={'all'} value={''}>All</MenuItem>
                            <MenuItem key={'yes'} value={'true'}>Yes</MenuItem>
                            <MenuItem key={'no'} value={'false'}>No</MenuItem>
                        </Select>
                    </FormControl>
                </InputWrapper>
                {isLoading ? <Placeholders />
                    :
                    <>
                        {questions.length > 0 ?
                            <>
                                {isMobile ?
                                    <MobileWrapper>
                                        {questions.map((question: QuestionType) => 
                                            <Question key={question.id} data={question} refresh={search} />
                                        )}
                                    </MobileWrapper>
                                    :
                                    <>
                                        <InfoRow>
                                            <p>ID</p>
                                            <p>Client</p>
                                            <p>Subject</p>
                                            <p>Date</p>
                                            <p>Answered</p>
                                        </InfoRow>
                                        {questions.map((question: QuestionType) => 
                                            <Question key={question.id} data={question} refresh={search} />
                                        )}
                                    </>
                                }
                                <PaginationWrapper>
                                    <TablePagination
                                        component="div"
                                        count={totalResults}
                                        page={page}
                                        onChangePage={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onChangeRowsPerPage={handleChangeRowsPerPage}
                                    />
                                </PaginationWrapper>
                            </>
                            : <NoDataPlaceholder pageName="Support" search={isSearching} />
                        }
                    </>
                }
            </Container>
        </>
    )
}
